import { defineComponent, onMounted, ref } from 'vue'
import {
  saveBackgroundTemplate,
  deleteBackgroundTemplate,
  sortBackgroundTemplate,
  getBackgroundTemplateList,
  getUploadTempSignature
} from './api'

function pickTemplateId(item) {
  if (!item) return null
  const id = item.id != null ? item.id : item.template_id != null ? item.template_id : item.templateId
  if (id == null) return null
  const n = Number(id)
  return Number.isFinite(n) ? n : null
}

function pickSortOrder(item) {
  if (!item) return 0
  const raw =
    item.sort_order != null
      ? item.sort_order
      : item.sortOrder != null
        ? item.sortOrder
        : item.sort
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

function normalizeTemplateList(list) {
  const rows = (list || []).map((item) => ({
    ...item,
    sort_order: pickSortOrder(item)
  }))
  rows.sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order
    const aid = pickTemplateId(a) || 0
    const bid = pickTemplateId(b) || 0
    return aid - bid
  })
  return rows
}

export default defineComponent({
  name: 'BackgroundView',
  setup() {
    const templateList = ref([])
    const loading = ref(false)
    const uploading = ref(false)
    const fileInputRef = ref(null)
    const previewVisible = ref(false)
    const previewUrl = ref('')
    const sortSavingId = ref(null)

    const fetchTemplateList = async () => {
      loading.value = true
      try {
        const response = await getBackgroundTemplateList()
        templateList.value = normalizeTemplateList(response.data || [])
      } catch (error) {
        console.error('获取背景模板失败:', error)
        alert('获取背景模板失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const triggerUpload = () => {
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
        fileInputRef.value.click()
      }
    }

    const extractPresignedUrl = (signatureResponse) => {
      const source = signatureResponse?.data || signatureResponse || {}
      const presignedUrl = source.url

      if (!presignedUrl) {
        throw new Error('上传凭证缺少 url 字段，请检查接口返回')
      }

      return presignedUrl
    }

    const uploadToCos = async (file) => {
      const signatureResponse = await getUploadTempSignature(file.name)
      const presignedUrl = extractPresignedUrl(signatureResponse)

      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      })

      if (!uploadResponse.ok) {
        throw new Error(`上传 COS 失败: ${uploadResponse.status}`)
      }

      return presignedUrl.split('?')[0]
    }

    const handleSelectFile = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      uploading.value = true
      try {
        const imageUrl = await uploadToCos(file)
        await saveBackgroundTemplate(imageUrl)
        alert('背景模板上传并绑定成功')

        await fetchTemplateList()
      } catch (error) {
        console.error('上传背景模板失败:', error)
        alert(error.message || '上传失败，请稍后重试')
      } finally {
        uploading.value = false
      }
    }

    const handleSortSave = async (template) => {
      const templateId = pickTemplateId(template)
      if (templateId == null) {
        alert('缺少模板 ID')
        return
      }
      const sort = pickSortOrder(template)
      if (!Number.isFinite(sort)) {
        alert('请输入有效的展示顺序')
        return
      }

      sortSavingId.value = templateId
      try {
        const response = await sortBackgroundTemplate(templateId, sort)
        const data = response.data || {}
        template.sort_order =
          data.sort_order != null
            ? Number(data.sort_order)
            : data.sortOrder != null
              ? Number(data.sortOrder)
              : sort
        templateList.value = normalizeTemplateList(templateList.value)
        alert('展示顺序已更新')
      } catch (error) {
        console.error('更新展示顺序失败:', error)
        alert(error.message || '更新展示顺序失败，请稍后重试')
      } finally {
        sortSavingId.value = null
      }
    }

    const handleDelete = async (template) => {
      if (!confirm('确定删除这张背景模板吗？')) return

      loading.value = true
      try {
        await deleteBackgroundTemplate(template.id)
        alert('删除成功')
        await fetchTemplateList()
      } catch (error) {
        console.error('删除背景模板失败:', error)
        alert('删除失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const openPreview = (imageUrl) => {
      previewUrl.value = imageUrl
      previewVisible.value = true
    }

    const closePreview = () => {
      previewVisible.value = false
      previewUrl.value = ''
    }

    onMounted(() => {
      fetchTemplateList()
    })

    return {
      templateList,
      loading,
      uploading,
      sortSavingId,
      handleSortSave,
      fileInputRef,
      previewVisible,
      previewUrl,
      triggerUpload,
      handleSelectFile,
      handleDelete,
      openPreview,
      closePreview
    }
  }
})
